import {
  VIEW_ITEM,
  PRICE_ALERT_STARTED,
  PRICE_ALERT_ADDED,
  PRICE_ALERT_EDITED,
  PRICE_ALERT_REACTIVATED,
  PRICE_ALERT_CANCELLED,
  PRICE_ALERT_BULK_CLEARED,
} from '../../../constants/gtmEvents';

/**
 * Centralized price-alert GTM event dispatchers.
 *
 * Each function follows the codebase's gtmHelper convention: it receives the
 * `ecommerceGtmEvent` dispatcher (from `useGtmHook()`) in its params object and
 * builds the event payload. Keeping every price-alert event here means the
 * payload shape, wording, and derived fields (notification type, pct below
 * market, timestamps) live in one place rather than being duplicated inline
 * across AlertEntry.js and AllAlertsList.js.
 */

// How the notification channel is worded everywhere in the price-alert UI:
// email is always on, SMS is the optional add-on.
const notificationType = (isSmsChecked) =>
  isSmsChecked ? 'Email + Text' : 'Email';

// How far the target sits below the current market price at submission:
// (current - target) / current * 100. Guard against a missing/zero current
// price so we never emit NaN/Infinity.
const pctBelowMarket = (currentPrice, targetPrice) =>
  currentPrice
    ? Math.round(((currentPrice - targetPrice) / currentPrice) * 100 * 100) /
      100
    : null;

// Where a VIEW_ITEM (trackBuyStarted) originated. Kept as a single vocabulary
// so the five call sites can't drift on spelling.
export const ENTRY_POINTS = {
  EMAIL_TEXT: 'email_text', // email/SMS deep-link buy (InfiniteHits)
  ACTIVE_ALERTS: 'active_alerts', // "Buy now" in the price-alerts modal
  PORTFOLIO_PRODUCT_PAGE: 'portfolio_product_page', // "Buy more" on position detail
  PRODUCT_DIRECT_BUY: 'product_direct_buy', // "Buy" on a Buy-page product card
  PRICE_ALERT_SET: 'price_alert_set', // a fresh alert was created (not a buy)
  MY_ALERTS: 'my_alerts', // price_alert_started source: bell / My Alerts list
};

// VIEW_ITEM — a buy journey started. Fired on any buy-button click
// (ProductCard, ProductPage), on "Buy now" from a triggered alert
// (AllAlertsList), on the email/SMS deep-link buy (InfiniteHits), and when a
// fresh alert is created (AlertEntry). alert_id is included only when the click
// originated from a price alert; entry_point (see ENTRY_POINTS) records which
// surface it came from. Both are omitted when not provided.
// source is a coarser dimension than entry_point: the notification channel a
// price-alert deep-link buy came through ('email' | 'sms' | 'text', from the
// &source deep-link param), null otherwise. Unlike the above it is always
// emitted so the null case is explicit in analytics.
// Notification channels a price-alert deep link can carry as `source`.
const DEEP_LINK_CHANNELS = ['email', 'sms', 'text'];

// entry_points whose buy journey began on a price-alert surface.
const PRICE_ALERT_ENTRY_POINTS = [
  ENTRY_POINTS.ACTIVE_ALERTS,
  ENTRY_POINTS.EMAIL_TEXT,
  ENTRY_POINTS.PRICE_ALERT_SET,
];

// Resolve the buy `source`: keep an explicit deep-link channel; otherwise any
// buy that originated from a price-alert flow is attributed to 'my_alerts'.
// Non-alert buys (product card, portfolio) keep their source (null). Shared so
// buy_started and the mirrored buy_submitted stay in sync.
export const resolveBuyStartedSource = (source, entryPoint) => {
  if (DEEP_LINK_CHANNELS.includes(source)) return source;
  if (PRICE_ALERT_ENTRY_POINTS.includes(entryPoint)) {
    return ENTRY_POINTS.MY_ALERTS;
  }
  return source ?? null;
};

export const trackBuyStarted = ({
  ecommerceGtmEvent,
  alertId,
  productName,
  entryPoint,
  source = null,
}) => {
  const resolvedSource = resolveBuyStartedSource(source, entryPoint);
  ecommerceGtmEvent(VIEW_ITEM, {
    ...(alertId != null && { alert_id: alertId }),
    product_name: productName,
    ...(entryPoint != null && { entry_point: entryPoint }),
    source: resolvedSource,
  });
};

// price_alert_added — a successful POST created a new alert record. Fires for
// both a fresh alert and a reactivation (both POST a new record).
export const trackPriceAlertAdded = ({
  ecommerceGtmEvent,
  alertId,
  productName,
  alertPrice,
  isSmsChecked,
  currentPrice,
}) => {
  ecommerceGtmEvent(PRICE_ALERT_ADDED, {
    alert_id: alertId,
    product_name: productName,
    alert_price: alertPrice,
    notification_type: notificationType(isSmsChecked),
    pct_below_market: pctBelowMarket(currentPrice, alertPrice),
  });
};

// price_alert_started — the user began an alert-setting journey. Fired when the
// Alert Entry screen opens (and on reactivation, a fresh journey too).
// source records where the journey began: 'my_alerts' (bell / My Alerts list,
// including "Add Another"), the notification channel for an email/SMS deep link
// ('email' | 'sms' | 'text'), or null. Always emitted so the null case is
// explicit in analytics.
export const trackPriceAlertStarted = ({
  ecommerceGtmEvent,
  productName,
  weight,
  currentPrice,
  source = null,
}) => {
  ecommerceGtmEvent(PRICE_ALERT_STARTED, {
    product_name: productName,
    weight,
    current_price: currentPrice,
    source,
    timestamp: new Date().toISOString(),
  });
};

// price_alert_edited — a single event carrying every changed field, keyed
// descriptively (old_price/new_price, old_channel/new_channel). `changes` holds
// only the fields that actually changed, so a price-only edit omits the channel
// keys and vice versa. Emits nothing when nothing changed.
export const trackPriceAlertEdited = ({
  ecommerceGtmEvent,
  alertId,
  changes = {},
  source = null,
}) => {
  if (Object.keys(changes).length === 0) return;
  ecommerceGtmEvent(PRICE_ALERT_EDITED, {
    alert_id: alertId,
    ...changes,
    source,
  });
};

// price_alert_reactivated — an expired alert renewed via "Set Again".
// alert_id references the original expired alert; the newly created record's
// uid is reported by trackPriceAlertAdded.
export const trackPriceAlertReactivated = ({
  ecommerceGtmEvent,
  alertId,
  originalAlertPrice,
  newAlertPrice,
  source = null,
}) => {
  ecommerceGtmEvent(PRICE_ALERT_REACTIVATED, {
    alert_id: alertId,
    original_alert_price: originalAlertPrice,
    new_alert_price: newAlertPrice,
    source,
  });
};

// price_alert_cancelled — a single active alert deleted by the user.
export const trackPriceAlertCancelled = ({
  ecommerceGtmEvent,
  alertId,
  alertPrice,
  timeActiveDays,
  source = null,
}) => {
  ecommerceGtmEvent(PRICE_ALERT_CANCELLED, {
    alert_id: alertId,
    alert_price: alertPrice,
    time_active_days: timeActiveDays,
    source,
  });
};

// price_alert_bulk_cleared — all expired alerts cleared at once.
export const trackPriceAlertBulkCleared = ({
  ecommerceGtmEvent,
  countCleared,
  alertIds,
}) => {
  ecommerceGtmEvent(PRICE_ALERT_BULK_CLEARED, {
    count_cleared: countCleared,
    alert_ids: alertIds,
  });
};
